import {
  useBillingCredit,
  useCancelScheduledChange,
  useCancelSubscription,
  useCreateCheckout,
  useMe,
  usePreviewCancelSubscription,
  type Formule,
  type Periode,
} from '@fidalia/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardDescription,
  CardTitle,
  cn,
  IconBadge,
  Modal,
  PageHeader,
  Spinner,
} from '@fidalia/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import {
  MdApartment,
  MdArrowForward,
  MdAutorenew,
  MdCardGiftcard,
  MdCheck,
  MdCreditCard,
  MdCreditCardOff,
  MdCreditScore,
  MdEventBusy,
  MdGroups,
  MdPerson,
  MdReceiptLong,
  MdTrendingDown,
  MdTrendingUp,
  MdUndo,
  MdWorkspacePremium,
} from 'react-icons/md';

import { useConfetti } from '@/features/onboarding/use-confetti';
import { track } from '@/lib/analytics';
import { errorMessage } from '@/lib/auth';

export const Route = createFileRoute('/_app/abonnement/')({
  component: AbonnementPage,
});

type Offre = {
  id: 'gratuit' | Formule;
  nom: string;
  icon: ReactNode;
  prixMensuel: number;
  prixAnnuel: number;
  description: string;
  fonctionnalites: string[];
};

/**
 * Ce qui ne dépend d'aucune formule. Répété sur chaque carte plutôt que relégué
 * en note de bas de page : c'est ce que les gens cherchent à vérifier avant de
 * choisir, et son absence d'une carte se lirait comme une restriction.
 */
const INCLUS_PARTOUT = ['Écrans illimités', 'Messages automatiques illimités'] as const;

const OFFRES: Offre[] = [
  {
    id: 'gratuit',
    nom: 'Essai gratuit',
    icon: <MdCardGiftcard />,
    prixMensuel: 0,
    prixAnnuel: 0,
    description: 'Un mois pour tester Fidalia sur votre agenda, sans engagement.',
    fonctionnalites: ['1 praticien', ...INCLUS_PARTOUT],
  },
  {
    id: 'solo',
    nom: 'Solo',
    icon: <MdPerson />,
    prixMensuel: 49,
    prixAnnuel: 39,
    description: 'Un praticien. L’essentiel de Fidalia pour un cabinet individuel.',
    fonctionnalites: ['1 praticien', ...INCLUS_PARTOUT],
  },
  {
    id: 'cabinet',
    nom: 'Cabinet',
    icon: <MdGroups />,
    prixMensuel: 99,
    prixAnnuel: 79,
    description: 'Jusqu’à cinq praticiens, chacun avec ses propres textes.',
    fonctionnalites: ['5 praticiens', ...INCLUS_PARTOUT],
  },
  {
    id: 'centre',
    nom: 'Centre',
    icon: <MdApartment />,
    prixMensuel: 149,
    prixAnnuel: 119,
    description: 'Sans limite de praticiens, pour les structures de groupe.',
    fonctionnalites: ['Praticiens illimités', ...INCLUS_PARTOUT],
  },
];

/** Ordre des formules payantes, du moins cher au plus cher. */
const RANG: Record<Formule, number> = { solo: 1, cabinet: 2, centre: 3 };

const NOM_FORMULE: Record<Formule, string> = { solo: 'Solo', cabinet: 'Cabinet', centre: 'Centre' };

function formatEuros(montant: number): string {
  return montant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Échéance de l'essai, au passé ou au futur.
 *
 * Les deux informations sont données ensemble : « dans 12 jours » se saisit
 * d'un coup d'œil mais ne se note pas dans un agenda, la date seule oblige à
 * compter. Le passé bascule au passé composé — « expire le » pour une date
 * révolue laisserait croire qu'il reste du temps.
 */
function echeanceEssai(finEssai: string): string {
  const fin = new Date(finEssai);
  const jours = Math.ceil((fin.getTime() - Date.now()) / (24 * 60 * 60 * 1000));

  if (jours <= 0) return `Expiré le ${formatDateCourte(finEssai)}`;
  if (jours === 1) return `Expire demain • ${formatDateCourte(finEssai)}`;

  return `Expire dans ${jours} jours • ${formatDateCourte(finEssai)}`;
}

/** Mois abrégé : la date accompagne un décompte, elle ne doit pas le dominer. */
function formatDateCourte(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Ce que le clic va déclencher, décidé côté client pour savoir s'il faut
 * confirmer.
 *
 * `caisse` part chez Stripe, qui affiche lui-même le montant et demande la
 * validation : redemander avant serait une confirmation de plus pour rien.
 * Les trois autres sont appliqués par nous, sans écran intermédiaire — c'est
 * là qu'une confirmation manque.
 */
type ActionOffre = 'caisse' | 'immediat' | 'programme' | 'reprise';

function actionPour(
  offre: Offre,
  cabinet: NonNullable<ReturnType<typeof useMe>['data']>['cabinet'] | undefined,
): ActionOffre {
  if (offre.id === 'gratuit') return 'caisse';
  if (!cabinet || cabinet.statut !== 'actif' || !cabinet.formule) return 'caisse';
  if (cabinet.formule === offre.id) return cabinet.annulationProgrammee ? 'reprise' : 'caisse';

  return RANG[offre.id] > RANG[cabinet.formule] ? 'immediat' : 'programme';
}

/**
 * Ce que la confirmation doit annoncer. Le prorata exact est calculé par
 * Stripe et reste inconnu ici : on annonce donc le tarif de la nouvelle
 * formule et la règle appliquée, jamais un montant inventé.
 */
function detailConfirmation(params: {
  offre: Offre;
  action: ActionOffre;
  prix: number;
  estAnnuel: boolean;
  periodeFin: string | null;
  formuleActuelle: Formule | null;
}): {
  titre: string;
  texte: ReactNode;
  bouton: string;
  danger: boolean;
  icone: ReactNode;
  tone: 'brand' | 'danger';
} {
  const { offre, action, prix, estAnnuel, periodeFin, formuleActuelle } = params;

  /**
   * En annuel, le prix affiché sur les cartes est mensualisé alors que le
   * prélèvement est unique : sans le total, on croit payer 39 € et on en voit
   * 468 partir. La somme réellement débitée passe donc avant le prix mensuel.
   */
  const tarif = estAnnuel ? (
    <>
      <strong className="font-semibold text-content">{formatEuros(prix * 12)} HT par an</strong>,
      prélevés en une fois ({formatEuros(prix)} HT/mois)
    </>
  ) : (
    <strong className="font-semibold text-content">{formatEuros(prix)} HT par mois</strong>
  );
  const nouvelle = <strong className="font-semibold text-content">{offre.nom}</strong>;
  const actuelle = formuleActuelle ? (
    <strong className="font-semibold text-content">{NOM_FORMULE[formuleActuelle]}</strong>
  ) : (
    'votre formule actuelle'
  );
  const echeance = periodeFin ? (
    <strong className="font-semibold text-content">{formatDate(periodeFin)}</strong>
  ) : (
    'la fin de la période en cours'
  );

  if (action === 'reprise') {
    return {
      titre: `Reprendre l’abonnement ${offre.nom} ?`,
      texte: (
        <>
          <span className="block">Votre résiliation sera annulée.</span>
          <span className="mt-2 block">
            Votre abonnement {nouvelle} se renouvellera normalement, à {tarif}.
          </span>
        </>
      ),
      bouton: 'Reprendre l’abonnement',
      danger: false,
      icone: <MdAutorenew />,
      tone: 'brand',
    };
  }

  if (action === 'immediat') {
    return {
      titre: `Passer en formule ${offre.nom} ?`,
      texte: (
        <>
          <span className="block">
            Le changement est <strong className="font-semibold text-content">immédiat</strong>. La
            différence avec la formule {actuelle} vous sera facturée{' '}
            <strong className="font-semibold text-content">aujourd’hui</strong>, au prorata des
            jours restants.
          </span>
          <span className="mt-2 block">Votre abonnement passe ensuite à {tarif}.</span>
        </>
      ),
      bouton: 'Confirmer le changement',
      danger: false,
      icone: <MdTrendingUp />,
      tone: 'brand',
    };
  }

  return {
    titre: `Rétrograder vers ${offre.nom} ?`,
    texte: (
      <>
        <span className="block">
          Vous gardez la formule {actuelle} » jusqu’{periodeFin ? 'au ' : 'à '}
          {echeance}, déjà payée. La formule « {nouvelle} » prendra effet à cette date, à {tarif}.
        </span>
        <span className="mt-2 block">Rien n’est facturé ni remboursé maintenant.</span>
      </>
    ),
    bouton: 'Confirmer la rétrogradation',
    danger: true,
    icone: <MdTrendingDown />,
    tone: 'danger',
  };
}

type StatutAlerte = { tone: 'info' | 'danger'; title: string; message: string };

function statutAlerte(
  cabinet: NonNullable<ReturnType<typeof useMe>['data']>['cabinet'],
): StatutAlerte | null {
  switch (cabinet.statut) {
    // Essai et abonnement actif se lisent déjà sur les cards ci-dessous :
    // pas de bannière en plus.
    case 'essai':
    case 'actif':
      return null;
    case 'impaye':
      return {
        tone: 'danger',
        title: 'Paiement en échec',
        message: 'Mettez à jour votre moyen de paiement pour éviter une interruption de service.',
      };
    case 'resilie':
      return {
        tone: 'danger',
        title: 'Abonnement résilié',
        message: 'Choisissez une formule ci-dessous pour réactiver votre compte.',
      };
  }
}

function AbonnementPage() {
  const { data: me, refetch: refetchMe } = useMe();
  const navigate = useNavigate();
  const checkout = useCreateCheckout();
  const annulationChangement = useCancelScheduledChange();
  const apercuResiliation = usePreviewCancelSubscription();
  const resiliation = useCancelSubscription();
  const { data: credit, refetch: refetchCredit } = useBillingCredit();

  const [periode, setPeriode] = useState<Periode>('annuel');
  // Lecture pure de l'URL : la mutation (nettoyer l'URL) est déportée dans
  // l'effet ci-dessous, jamais pendant le rendu.
  const [retourCheckout] = useState<'success' | 'cancel' | null>(() => {
    const valeur = new URLSearchParams(window.location.search).get('checkout');
    return valeur === 'success' || valeur === 'cancel' ? valeur : null;
  });
  const [modalSuccesFermee, setModalSuccesFermee] = useState(false);
  const [modalAnnuleeFermee, setModalAnnuleeFermee] = useState(false);

  /**
   * Action en attente de confirmation. Tout ce qui est appliqué par nous passe
   * par là : sans écran Stripe intermédiaire, un clic seul suffirait à changer
   * une formule facturée.
   */
  const [aConfirmer, setAConfirmer] = useState<{ offre: Offre; action: ActionOffre } | null>(null);
  const [annulationAConfirmer, setAnnulationAConfirmer] = useState(false);
  const [resiliationAConfirmer, setResiliationAConfirmer] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // Changement appliqué sans passer par Stripe : rien ne redirige, donc rien
  // ne confirme à l'écran si on ne le dit pas.
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const modalSuccesOuverte = retourCheckout === 'success' && !modalSuccesFermee;
  const modalAnnuleeOuverte = retourCheckout === 'cancel' && !modalAnnuleeFermee;
  useConfetti(modalSuccesOuverte);

  // Le retour de Stripe (`?checkout=success|cancel`) ne doit s'afficher
  // qu'une fois : l'URL est nettoyée aussitôt affichée, pour ne pas
  // réapparaître au rafraîchissement de la page. Le webhook peut avoir
  // légèrement de retard sur la redirection : un rafraîchissement de la
  // session met à jour le badge de statut dès qu'il arrive.
  useEffect(() => {
    if (!retourCheckout) return;
    window.history.replaceState(null, '', '/abonnement');
    track('checkout_returned', { result: retourCheckout });
    if (retourCheckout === 'success') void refetchMe();
  }, [retourCheckout, refetchMe]);

  const cabinet = me?.cabinet;
  const alerte = cabinet ? statutAlerte(cabinet) : null;
  const estGold = cabinet?.gold === true;
  const estAnnuel = periode === 'annuel';

  /**
   * Point d'entrée des boutons de carte. Confirme d'abord si l'action est
   * appliquée par nous, sinon enchaîne directement — Stripe fera confirmer.
   */
  const demanderOffre = (offre: Offre) => {
    const action = actionPour(offre, cabinet);
    if (action === 'caisse') return void choisirOffre(offre);
    setAConfirmer({ offre, action });
  };

  const choisirOffre = async (offre: Offre) => {
    if (offre.id === 'gratuit') return;
    setErreur(null);
    setConfirmation(null);
    setAConfirmer(null);
    try {
      const resultat = await checkout.mutateAsync({ formule: offre.id, periode });
      track('checkout_started', { plan: offre.id, period: periode, action: resultat.action });

      // Première souscription : Stripe encaisse, la page est quittée.
      if (resultat.action === 'caisse') {
        window.location.assign(resultat.url);
        return;
      }

      // Changement sur un abonnement existant : on reste ici, il faut donc
      // recharger l'état et dire ce qui vient de se passer.
      await Promise.all([refetchMe(), refetchCredit()]);

      // Rien à confirmer sur un changement programmé : la bannière « Changement
      // de formule programmé » apparaît au rechargement et porte déjà la date.
      if (resultat.action === 'change_immediat') {
        setConfirmation(
          `Vous êtes passé en formule ${offre.nom}. La différence de prix a été facturée au prorata de la période en cours.`,
        );
      }
    } catch (cause) {
      setErreur(errorMessage(cause, "Impossible d'ouvrir le paiement."));
    }
  };

  const annulerChangement = async () => {
    setErreur(null);
    setConfirmation(null);
    setAnnulationAConfirmer(false);
    try {
      await annulationChangement.mutateAsync();
      await refetchMe();
      setConfirmation('Changement annulé : votre formule actuelle se renouvellera normalement.');
    } catch (cause) {
      setErreur(errorMessage(cause, "Impossible d'annuler le changement."));
    }
  };

  const ouvrirResiliation = () => {
    setErreur(null);
    setResiliationAConfirmer(true);
    void apercuResiliation.refetch();
  };

  const confirmerResiliation = async () => {
    setErreur(null);
    setConfirmation(null);
    try {
      const { dateFin, montantCredit } = await resiliation.mutateAsync();
      setResiliationAConfirmer(false);
      await Promise.all([refetchMe(), refetchCredit()]);
      setConfirmation(
        montantCredit > 0
          ? `Abonnement résilié. Accès conservé jusqu’au ${formatDate(dateFin)}, ${formatEuros(montantCredit)} crédités en avoir Fidalia.`
          : `Abonnement résilié. Accès conservé jusqu’au ${formatDate(dateFin)}.`,
      );
    } catch (cause) {
      setErreur(errorMessage(cause, "Impossible de résilier l'abonnement."));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdCreditCard />}
        title="Abonnement"
        description="Votre formule, vos factures et votre échéance."
        actions={
          <Button
            variant="primary"
            icon={<MdReceiptLong className="size-5" />}
            onClick={() => navigate({ to: '/abonnement/paiements' })}
          >
            Mes paiements
          </Button>
        }
      />

      {/* Pendant du retour réussi : Stripe renvoie ici sans rien dire, une
          bannière discrète passerait inaperçue après une redirection. */}
      <Modal
        open={modalAnnuleeOuverte}
        onClose={() => setModalAnnuleeFermee(true)}
        size="lg"
        align="center"
        icon={
          <IconBadge size="2xl" tone="danger">
            <MdCreditCardOff />
          </IconBadge>
        }
        title="Paiement annulé"
        description={
          <span className="mx-auto block max-w-md text-balance">
            Aucun montant n’a été débité et votre formule n’a pas changé. Vous pouvez reprendre la
            souscription quand vous le souhaitez.
          </span>
        }
        footer={
          <Button size="lg" variant="outline" fullWidth onClick={() => setModalAnnuleeFermee(true)}>
            Revenir aux formules
          </Button>
        }
      />

      {/* Confirmation de tout changement appliqué sans passer par Stripe.
          Monté seulement quand une action attend : le contenu dépend d'elle,
          et un `Modal` fermé garderait l'ancien texte le temps de l'animation. */}
      {aConfirmer
        ? (() => {
            const prix = estAnnuel ? aConfirmer.offre.prixAnnuel : aConfirmer.offre.prixMensuel;
            const detail = detailConfirmation({
              offre: aConfirmer.offre,
              action: aConfirmer.action,
              prix,
              estAnnuel,
              periodeFin: cabinet?.periodeFin ?? null,
              formuleActuelle: cabinet?.formule ?? null,
            });

            return (
              <Modal
                open
                onClose={() => setAConfirmer(null)}
                size="md"
                icon={<IconBadge tone={detail.tone}>{detail.icone}</IconBadge>}
                title={detail.titre}
                description={detail.texte}
                footer={
                  <div className="flex justify-end gap-3">
                    <Button variant="ghost" onClick={() => setAConfirmer(null)}>
                      Annuler
                    </Button>
                    <Button
                      variant={detail.danger ? 'danger' : 'primary'}
                      loading={checkout.isPending}
                      onClick={() => void choisirOffre(aConfirmer.offre)}
                    >
                      {detail.bouton}
                    </Button>
                  </div>
                }
              />
            );
          })()
        : null}

      <Modal
        open={annulationAConfirmer}
        onClose={() => setAnnulationAConfirmer(false)}
        size="md"
        icon={
          <IconBadge tone="brand">
            <MdUndo />
          </IconBadge>
        }
        title="Annuler le changement programmé ?"
        description={
          cabinet?.formuleProgrammee && cabinet.periodeFin
            ? `Votre passage en formule ${NOM_FORMULE[cabinet.formuleProgrammee]} du ${formatDate(
                cabinet.periodeFin,
              )} sera abandonné. Votre formule actuelle se renouvellera normalement à cette date.`
            : 'Votre formule actuelle se renouvellera normalement.'
        }
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setAnnulationAConfirmer(false)}>
              Revenir
            </Button>
            <Button
              loading={annulationChangement.isPending}
              onClick={() => void annulerChangement()}
            >
              Annuler le changement
            </Button>
          </div>
        }
      />

      <Modal
        open={resiliationAConfirmer}
        onClose={() => setResiliationAConfirmer(false)}
        size="md"
        icon={
          <IconBadge tone="danger">
            <MdEventBusy />
          </IconBadge>
        }
        title="Résilier l’abonnement ?"
        description={
          apercuResiliation.isFetching || !apercuResiliation.data ? (
            <span className="flex items-center gap-2">
              <Spinner className="size-4" /> Calcul de l’avoir en cours…
            </span>
          ) : (
            <>
              <span className="block">
                Vous gardez l’accès jusqu’au{' '}
                <strong className="font-semibold text-content">
                  {formatDate(apercuResiliation.data.dateFin)}
                </strong>{' '}
                (un mois de préavis).
              </span>
              {apercuResiliation.data.montantCredit > 0 ? (
                <span className="mt-2 block">
                  <strong className="font-semibold text-content">
                    {formatEuros(apercuResiliation.data.montantCredit)}
                  </strong>{' '}
                  correspondant à la période déjà payée et non consommée vous seront crédités en
                  avoir Fidalia, utilisable sur vos prochaines factures.
                </span>
              ) : null}
            </>
          )
        }
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setResiliationAConfirmer(false)}>
              Revenir
            </Button>
            <Button
              variant="danger"
              loading={resiliation.isPending}
              disabled={!apercuResiliation.data}
              onClick={() => void confirmerResiliation()}
            >
              Confirmer la résiliation
            </Button>
          </div>
        }
      />

      {erreur ? <Alert tone="danger">{erreur}</Alert> : null}

      {confirmation ? <Alert tone="success">{confirmation}</Alert> : null}

      {alerte ? (
        <Alert tone={alerte.tone} title={alerte.title}>
          {alerte.message}
        </Alert>
      ) : null}

      {cabinet?.formuleProgrammee && cabinet.periodeFin ? (
        <Alert tone="info" title="Changement de formule programmé">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            Vous passerez en formule {NOM_FORMULE[cabinet.formuleProgrammee]} le{' '}
            {formatDate(cabinet.periodeFin)}, au renouvellement. Jusque-là, rien ne change.
            <button
              type="button"
              disabled={annulationChangement.isPending}
              onClick={() => setAnnulationAConfirmer(true)}
              className="font-semibold underline underline-offset-2 disabled:opacity-60"
            >
              Annuler ce changement
            </button>
          </span>
        </Alert>
      ) : null}

      {credit && credit.montant > 0 ? (
        <Alert tone="info" title={`${formatEuros(credit.montant)} de crédit Fidalia`}>
          Suite à un changement de formule, cette somme vous a été remboursée en crédit Fidalia.
          Vous pouvez l’utiliser pour régler vos prochaines factures : elle en sera déduite
          automatiquement, jusqu’à épuisement.
        </Alert>
      ) : null}

      <div className="flex justify-center">
        <Card role="tablist" className="inline-flex gap-1 bg-surface-muted p-1.5 shadow-sm">
          <button
            type="button"
            role="tab"
            aria-selected={!estAnnuel}
            onClick={() => setPeriode('mensuel')}
            className={cn(
              'rounded-field px-3 py-1.5 text-sm font-semibold transition-colors',
              !estAnnuel ? 'bg-surface text-content shadow-card' : 'text-content-subtle',
            )}
          >
            Mensuel
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={estAnnuel}
            onClick={() => setPeriode('annuel')}
            className={cn(
              'flex items-center gap-2 rounded-field px-3 py-1.5 text-sm font-semibold transition-colors',
              estAnnuel ? 'bg-surface text-content shadow-card' : 'text-content-subtle',
            )}
          >
            Annuel
            <Badge tone="accent" size="sm">
              −20 %
            </Badge>
          </button>
        </Card>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* Gold couvre déjà l'essai : la carte gratuite n'a plus de sens à
            côté d'un accès illimité, la carte Gold ci-dessous prend sa place
            en fin de grille. */}
        {(estGold ? OFFRES.filter((offre) => offre.id !== 'gratuit') : OFFRES).map((offre) => {
          const estGratuit = offre.id === 'gratuit';
          const estActuelle =
            !estGratuit && cabinet?.statut === 'actif' && cabinet.formule === offre.id;
          const prix = estAnnuel ? offre.prixAnnuel : offre.prixMensuel;
          const enCours = checkout.isPending && checkout.variables?.formule === offre.id;

          // Rang de la formule actuellement active, pour situer chaque carte
          // par rapport à elle (rétrograder vers moins cher, monter en gamme).
          const rangActuel =
            cabinet?.statut === 'actif' && cabinet.formule ? RANG[cabinet.formule] : null;
          const rangOffre = !estGratuit ? RANG[offre.id as Formule] : null;
          const estRetrogradation =
            rangActuel !== null && rangOffre !== null && rangOffre < rangActuel;
          const estProgrammee = !estGratuit && cabinet?.formuleProgrammee === offre.id;

          return (
            <Card
              key={offre.id}
              className={cn(
                'relative flex flex-col gap-5',
                estGratuit && 'border-0 bg-transparent shadow-none',
                estActuelle &&
                  'border-2 border-success shadow-[0_12px_36px_-6px_rgb(47_125_91/0.55)] ring-2 ring-success/25',
              )}
            >
              {estActuelle ? (
                cabinet.annulationProgrammee && cabinet.periodeFin ? (
                  <Badge tone="danger" className="absolute right-4 top-4">
                    Expire le {formatDate(cabinet.periodeFin)}
                  </Badge>
                ) : (
                  <Badge tone="success" className="absolute right-4 top-4">
                    Abonnement actif
                  </Badge>
                )
              ) : estProgrammee ? (
                <Badge tone="accent" className="absolute right-4 top-4">
                  Programmé
                </Badge>
              ) : null}

              <div>
                <div className="flex items-center gap-2">
                  <span className="text-brand-600 [&>svg]:size-5">{offre.icon}</span>
                  <CardTitle>{offre.nom}</CardTitle>
                </div>
                <CardDescription className="mt-2">{offre.description}</CardDescription>
                <div className="mt-4 flex items-baseline gap-1.5">
                  {estGratuit ? (
                    // Le titre de la carte dit déjà « Essai gratuit » : cet
                    // emplacement est celui du prix, il doit donner le prix.
                    <span className="text-4xl font-semibold text-content">Gratuit</span>
                  ) : (
                    <>
                      <span className="text-4xl font-semibold text-content">{prix} €</span>
                      <span className="text-sm text-content-subtle">HT/mois</span>
                    </>
                  )}
                </div>
              </div>

              {/* Sous Gold, aucun bouton n'a d'action réelle : toutes les
                  formules payantes sont déjà couvertes. Les faire disparaître
                  dit ça plus clairement qu'un bouton grisé sur chaque carte —
                  c'est la carte Gold, seule, qui porte l'information. */}
              {estGold ? null : estGratuit ? (
                // L'échéance remplace « Formule actuelle » : le bouton est
                // désactivé de toute façon, autant qu'il porte la seule
                // information utile de la carte.
                <Button variant="outline" fullWidth disabled>
                  {cabinet?.statut !== 'essai'
                    ? 'Réservé aux nouveaux comptes'
                    : cabinet.finEssai
                      ? echeanceEssai(cabinet.finEssai)
                      : 'Formule actuelle'}
                </Button>
              ) : estActuelle ? (
                cabinet.annulationProgrammee ? (
                  // L'échéance est déjà portée par le badge : le bouton sert à
                  // reprendre l'abonnement, pas à répéter la date. Repasser par
                  // la même formule lève la résiliation programmée côté Stripe,
                  // sans créer de second abonnement.
                  <Button fullWidth loading={enCours} onClick={() => demanderOffre(offre)}>
                    Se ré-abonner
                  </Button>
                ) : (
                  <Button variant="outline" fullWidth disabled>
                    Formule actuelle
                  </Button>
                )
              ) : estProgrammee && cabinet?.periodeFin ? (
                // Rétrogradation déjà programmée : la carte annonce sa date
                // d'effet, l'abandon se fait depuis la bannière en haut de page.
                <Button variant="outline" fullWidth disabled>
                  À partir du {formatDate(cabinet.periodeFin)}
                </Button>
              ) : (
                <Button
                  variant={estRetrogradation ? 'outline' : 'primary'}
                  fullWidth
                  loading={enCours}
                  onClick={() => demanderOffre(offre)}
                >
                  {estRetrogradation ? `Rétrograder vers ${offre.nom}` : 'S’abonner'}
                </Button>
              )}

              <ul className="flex flex-1 flex-col gap-2.5">
                {offre.fonctionnalites.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-content-muted">
                    <MdCheck className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>

              {estActuelle && !cabinet?.annulationProgrammee ? (
                <button
                  type="button"
                  onClick={ouvrirResiliation}
                  className="text-center text-xs text-content-subtle underline-offset-2 hover:text-danger hover:underline"
                >
                  Annuler l’abonnement
                </button>
              ) : null}
            </Card>
          );
        })}

        {estGold ? (
          <Card className="relative flex flex-col gap-5 border-2 border-gold-500 bg-gold-100/40 shadow-[0_12px_36px_-6px_rgb(184_125_18/0.4)] ring-2 ring-gold-500/25">
            <Badge tone="accent" className="absolute right-4 top-4">
              Formule active
            </Badge>

            <div>
              <div className="flex items-center gap-2">
                <span className="bg-gradient-to-br from-gold-300 via-gold-500 to-gold-700 bg-clip-text text-transparent [&>svg]:size-5 [&>svg]:fill-gold-500">
                  <MdWorkspacePremium />
                </span>
                <CardTitle>Gold</CardTitle>
              </div>
              <CardDescription className="mt-2">
                Un accès sur mesure, accordé par notre équipe. Aucune limite, nulle part.
              </CardDescription>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="text-4xl font-semibold text-content">Illimité</span>
              </div>
            </div>

            <ul className="flex flex-1 flex-col gap-2.5">
              {['Praticiens illimités', ...INCLUS_PARTOUT, 'Rien ne vous est facturé'].map(
                (item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-content-muted">
                    <MdCheck className="mt-0.5 size-4 shrink-0 text-gold-500" aria-hidden="true" />
                    {item}
                  </li>
                ),
              )}
            </ul>
          </Card>
        ) : null}
      </div>

      <Modal
        open={modalSuccesOuverte}
        onClose={() => setModalSuccesFermee(true)}
        size="lg"
        align="center"
        icon={
          <IconBadge size="2xl" tone="success">
            <MdCreditScore />
          </IconBadge>
        }
        title="Paiement réussi !"
        description={
          // `text-balance` répartit les lignes à égalité : sans lui, la
          // dernière ligne se retrouve avec un mot isolé.
          <span className="mx-auto block max-w-md text-balance">
            Votre abonnement est activé. Merci de votre confiance : vous avez accès à toutes les
            fonctionnalités de votre formule.
          </span>
        }
        footer={
          <Button
            size="lg"
            fullWidth
            icon={<MdArrowForward className="size-5" />}
            onClick={() => setModalSuccesFermee(true)}
          >
            Continuer vers Fidalia
          </Button>
        }
      />
    </div>
  );
}
